import React, { useEffect, useRef } from "react";
import {
  CDMQB_FONT_FAMILIES,
  CDMQB_FONT_SIZE_MAX,
  CDMQB_FONT_SIZE_MIN,
  CDMQB_TEXT_ZOOM_PRESETS,
} from "../../SimulationConstants";
import "./SettingsPanel.css";

// Settings panel for the default skin. Shares the settings state (and
// storage key) with the CDMQB panel, but only shows the options that the
// default skin actually uses: text size, font, and the skin switcher.
// Split layout / Confirm before Next / original lab instructions are
// CDMQB-only behaviors, so they're left out here.

export default function SettingsPanel(props) {
  const {
    displaySettings,
    closeSettings,
    settings,
    updateSetting,
    resetSettings,
    simulationSkin,
    simulationSkins,
    changeSimulationSkin,
  } = props;

  const panelRef = useRef(null);

  // Escape closes the panel; focus moves into it when it opens.
  useEffect(() => {
    if (!displaySettings) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeSettings();
    };
    document.addEventListener("keydown", onKeyDown);
    if (panelRef.current) panelRef.current.focus();
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [displaySettings, closeSettings]);

  if (!displaySettings) return null;

  const fontSize = settings.fontSize;
  const setFontSize = (size) => updateSetting("fontSize", size);

  return (
    <div className="simulationSettingsOverlay" onMouseDown={closeSettings}>
      <aside
        ref={panelRef}
        className="simulationSettingsPanel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="simulationSettingsTitle"
        tabIndex={-1}
        // Clicks inside the panel shouldn't reach the overlay.
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="simulationSettingsHeader">
          <h2 id="simulationSettingsTitle" className="simulationSettingsTitle">
            Settings
          </h2>
          <button
            type="button"
            className="simulationSettingsClose"
            onClick={closeSettings}
            aria-label="Close settings"
          >
            ×
          </button>
        </div>

        <div className="simulationSettingsBody">
          {/* Text size */}
          <section className="simulationSettingsSection">
            <h3 className="simulationSettingsLabel">Text size</h3>

            <div className="simulationSettingsPresets">
              {CDMQB_TEXT_ZOOM_PRESETS.map((preset) => (
                <button
                  key={preset.size}
                  type="button"
                  className={`simulationSettingsPreset ${
                    fontSize === preset.size ? "active" : ""
                  }`}
                  onClick={() => setFontSize(preset.size)}
                  aria-label={preset.label}
                  aria-pressed={fontSize === preset.size}
                  title={preset.label}
                >
                  <span style={{ fontSize: `${preset.previewSize}px` }}>A</span>
                </button>
              ))}
            </div>

            <div className="simulationSettingsStepper">
              <button
                type="button"
                onClick={() => setFontSize(fontSize - 1)}
                disabled={fontSize <= CDMQB_FONT_SIZE_MIN}
                aria-label="Decrease text size"
              >
                −
              </button>
              <span className="simulationSettingsStepperValue" aria-live="polite">
                {fontSize}px
              </span>
              <button
                type="button"
                onClick={() => setFontSize(fontSize + 1)}
                disabled={fontSize >= CDMQB_FONT_SIZE_MAX}
                aria-label="Increase text size"
              >
                +
              </button>
            </div>
          </section>

          {/* Font */}
          <section className="simulationSettingsSection">
            <label
              htmlFor="simulationSettingsFont"
              className="simulationSettingsLabel"
            >
              Font
            </label>
            <select
              id="simulationSettingsFont"
              className="simulationSettingsSelect"
              value={settings.fontFamily}
              onChange={(e) => updateSetting("fontFamily", e.target.value)}
            >
              {CDMQB_FONT_FAMILIES.map((font) => (
                <option
                  key={font.id}
                  value={font.id}
                  style={font.stack ? { fontFamily: font.stack } : undefined}
                >
                  {font.label}
                </option>
              ))}
            </select>
          </section>

          {/* Skin switcher (only if the app lets us change it) */}
          {changeSimulationSkin && simulationSkins && simulationSkins.length > 1 && (
            <section className="simulationSettingsSection">
              <label
                htmlFor="simulationSettingsSkin"
                className="simulationSettingsLabel"
              >
                Simulation Skin
              </label>
              <select
                id="simulationSettingsSkin"
                className="simulationSettingsSelect"
                value={simulationSkin || "default"}
                onChange={(e) => changeSimulationSkin(e.target.value)}
              >
                {simulationSkins.map((skin) => (
                  <option key={skin.id} value={skin.id}>
                    {skin.skinTitle}
                  </option>
                ))}
              </select>
            </section>
          )}
        </div>

        <div className="simulationSettingsFooter">
          <button
            type="button"
            className="simulationSettingsReset"
            onClick={resetSettings}
          >
            Reset to defaults
          </button>
          <button
            type="button"
            className="simulationSettingsDone"
            onClick={closeSettings}
          >
            Done
          </button>
        </div>
      </aside>
    </div>
  );
}
